import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { relatoriosConsultasAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Loader2, FileBarChart2, MapPin, CreditCard } from 'lucide-react';
import { CreditoBadge, fmtDoc, fmtDataHora } from '@/components/credito/CreditoResultado';
import { ServicoChip } from '@/components/viabilidade/servicos';

const iso = (d) => d.toISOString().slice(0, 10);
const hoje = () => new Date();
const PERIODOS = [
  { k: 'hoje', label: 'Hoje', calc: () => [iso(hoje()), iso(hoje())] },
  { k: '7d', label: '7 dias', calc: () => { const f = hoje(); const i = new Date(f); i.setDate(f.getDate() - 6); return [iso(i), iso(f)]; } },
  { k: 'mes', label: 'Mês atual', calc: () => { const f = hoje(); return [iso(new Date(f.getFullYear(), f.getMonth(), 1)), iso(f)]; } },
  { k: 'mes_ant', label: 'Mês anterior', calc: () => { const f = hoje(); return [iso(new Date(f.getFullYear(), f.getMonth() - 1, 1)), iso(new Date(f.getFullYear(), f.getMonth(), 0))]; } },
];
const ORIGEM = { tela: 'Tela', nova_venda: 'Nova Venda', venda: 'Venda' };

const Kpi = ({ label, valor, cor = '', testid }) => (
  <div className="rounded-lg border bg-white dark:bg-gray-900 px-3 py-2" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-xl font-bold ${cor}`}>{valor}</p>
  </div>
);

const TabelaViabilidade = ({ itens }) => (
  <div className="overflow-auto rounded-lg border max-h-[420px]">
    <table className="w-full text-xs" data-testid="rel-viab-tabela">
      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/90">
        <tr className="text-left text-gray-500 border-b">
          <th className="px-2 py-1.5 font-medium">Data</th><th className="px-2 py-1.5 font-medium">Colaborador</th><th className="px-2 py-1.5 font-medium">Etapa</th>
          <th className="px-2 py-1.5 font-medium">Endereço</th><th className="px-2 py-1.5 font-medium">Cidade</th><th className="px-2 py-1.5 font-medium">HP</th><th className="px-2 py-1.5 font-medium">Serviços</th><th className="px-2 py-1.5 font-medium">Origem</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((v) => (
          <tr key={v.id} className="border-b last:border-0 align-top" data-testid={`rel-viab-${v.id}`}>
            <td className="px-2 py-1.5 whitespace-nowrap">{fmtDataHora(v.criado_em)}</td>
            <td className="px-2 py-1.5">{v.criado_por}</td>
            <td className="px-2 py-1.5">{v.tipo === 'detalhe' ? <Badge className="bg-purple-600 hover:bg-purple-600">HP</Badge> : <Badge variant="outline">Endereço</Badge>}</td>
            <td className="px-2 py-1.5">{v.logradouro}, {v.numero}{v.tipo === 'detalhe' && v.complementos ? ` — ${v.complementos}` : ''}{v.bairro ? ` • ${v.bairro}` : ''}<span className="text-gray-400"> • CEP {v.cep}</span>{v.tipo !== 'detalhe' && <span className="text-gray-500"> • {v.total_hps} complemento(s)</span>}</td>
            <td className="px-2 py-1.5 whitespace-nowrap">{v.cidade ? `${v.cidade}/${v.uf}` : '-'}</td>
            <td className="px-2 py-1.5 font-mono">{v.hp || '-'}</td>
            <td className="px-2 py-1.5"><div className="flex flex-wrap gap-1">{(v.servicos || []).map((s) => <ServicoChip key={s.tipo} s={s} />)}</div></td>
            <td className="px-2 py-1.5">{ORIGEM[v.origem] || 'Tela'}</td>
          </tr>
        ))}
        {itens.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-500">Nenhuma consulta de viabilidade no período.</td></tr>}
      </tbody>
    </table>
  </div>
);

const TabelaCredito = ({ itens, navigate }) => (
  <div className="overflow-auto rounded-lg border max-h-[420px]">
    <table className="w-full text-xs" data-testid="rel-cred-tabela">
      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/90">
        <tr className="text-left text-gray-500 border-b">
          <th className="px-2 py-1.5 font-medium">Data</th><th className="px-2 py-1.5 font-medium">Colaborador</th><th className="px-2 py-1.5 font-medium">Cliente</th><th className="px-2 py-1.5 font-medium">Documento</th>
          <th className="px-2 py-1.5 font-medium">Cidade</th><th className="px-2 py-1.5 font-medium">Tipo</th><th className="px-2 py-1.5 font-medium">Resultado</th><th className="px-2 py-1.5 font-medium">Motivo (Claro)</th><th className="px-2 py-1.5 font-medium">Origem</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((c) => (
          <tr key={c.id} className="border-b last:border-0 align-top" data-testid={`rel-cred-${c.id}`}>
            <td className="px-2 py-1.5 whitespace-nowrap">{fmtDataHora(c.criado_em)}</td>
            <td className="px-2 py-1.5">{c.criado_por}</td>
            <td className="px-2 py-1.5">{c.nome}</td>
            <td className="px-2 py-1.5 font-mono whitespace-nowrap">{fmtDoc(c.cpf_cnpj)}</td>
            <td className="px-2 py-1.5 whitespace-nowrap">{c.cidade}/{c.uf}</td>
            <td className="px-2 py-1.5">{c.tipo_venda === 'BASE' ? `Base • ${c.numero_contrato}` : 'Novo cliente'} • {c.tipo_cliente === 'EMPRESARIAL' ? 'PJ' : 'PF'}</td>
            <td className="px-2 py-1.5"><CreditoBadge resultado={c.resultado} mensagem={c.mensagem} /></td>
            <td className="px-2 py-1.5">{c.mensagem}</td>
            <td className="px-2 py-1.5 whitespace-nowrap">
              {ORIGEM[c.origem] || 'Tela'}
              {c.venda_id && <button type="button" onClick={() => navigate(`/vendas/${c.venda_id}`)} className="ml-1 text-purple-600 hover:underline font-mono" data-testid={`rel-cred-venda-${c.id}`}>#{c.venda_protocolo || 'venda'}</button>}
            </td>
          </tr>
        ))}
        {itens.length === 0 && <tr><td colSpan={9} className="px-3 py-4 text-center text-gray-500">Nenhuma checagem de crédito no período.</td></tr>}
      </tbody>
    </table>
  </div>
);

export const RelatorioConsultas = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('mes');
  const [[dataInicio, dataFim], setDatas] = useState(PERIODOS[2].calc());
  const [userId, setUserId] = useState('');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const params = useMemo(() => ({ data_inicio: dataInicio, data_fim: dataFim, user_id: userId || undefined }), [dataInicio, dataFim, userId]);
  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setDados((await relatoriosConsultasAPI.consultas(params)).data); } catch (e) { toast.error(formatarErroAPI(e)); } finally { setCarregando(false); }
  }, [params]);
  useEffect(() => { carregar(); }, [carregar]);

  const exportar = async () => {
    setExportando(true);
    try {
      const res = await relatoriosConsultasAPI.exportar(params);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `consultas_claro_${dataInicio}_${dataFim}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatarErroAPI(e)); } finally { setExportando(false); }
  };

  const r = dados?.resumo;
  return (
    <div className="space-y-4" data-testid="relatorio-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><FileBarChart2 className="w-4 h-4" /> Relatório de consultas Claro</CardTitle>
              <CardDescription>Endereços consultados na viabilidade e checagens de crédito, por período e colaborador.</CardDescription>
            </div>
            <Button size="sm" onClick={exportar} disabled={exportando || !dados} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rel-exportar-button">
              {exportando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Exportar Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex gap-1" data-testid="rel-periodos">
              {PERIODOS.map((p) => (
                <Button key={p.k} size="sm" variant={periodo === p.k ? 'default' : 'outline'} className={periodo === p.k ? 'bg-purple-600 hover:bg-purple-700' : ''} onClick={() => { setPeriodo(p.k); setDatas(p.calc()); }} data-testid={`rel-periodo-${p.k}`}>{p.label}</Button>
              ))}
            </div>
            <div><Label className="text-xs">De</Label><Input type="date" value={dataInicio} onChange={(e) => { setPeriodo(''); setDatas([e.target.value, dataFim]); }} className="mt-1 h-9" data-testid="rel-data-inicio" /></div>
            <div><Label className="text-xs">Até</Label><Input type="date" value={dataFim} onChange={(e) => { setPeriodo(''); setDatas([dataInicio, e.target.value]); }} className="mt-1 h-9" data-testid="rel-data-fim" /></div>
            <div>
              <Label className="text-xs">Colaborador</Label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} className="mt-1 h-9 min-w-[200px] rounded-md border bg-white dark:bg-gray-900 px-2 text-sm" data-testid="rel-colaborador-select">
                <option value="">Todos</option>
                {(dados?.usuarios || []).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            {carregando && <Loader2 className="w-4 h-4 animate-spin text-gray-400 mb-2" />}
          </div>
          {r && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="rel-kpis">
              <Kpi label="Endereços consultados" valor={r.enderecos} testid="rel-kpi-enderecos" />
              <Kpi label="HPs detalhados" valor={r.hps} testid="rel-kpi-hps" />
              <Kpi label="HPs com produto" valor={r.com_produto} cor="text-emerald-600" testid="rel-kpi-com-produto" />
              <Kpi label="Checagens de crédito" valor={r.creditos} testid="rel-kpi-creditos" />
              <Kpi label="Aprovados" valor={r.aprovados} cor="text-emerald-600" testid="rel-kpi-aprovados" />
              <Kpi label="Reprovados N2 (cancela)" valor={r.analise_n2 || 0} cor="text-red-700" testid="rel-kpi-analise-n2" />
              <Kpi label="Reprovados" valor={r.reprovados} cor="text-red-600" testid="rel-kpi-reprovados" />
            </div>
          )}
          {r?.por_usuario?.length > 0 && (
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-xs" data-testid="rel-por-usuario">
                <thead className="bg-gray-50 dark:bg-gray-800/60"><tr className="text-left text-gray-500 border-b"><th className="px-2 py-1.5 font-medium">Colaborador</th><th className="px-2 py-1.5 font-medium text-right">Endereços</th><th className="px-2 py-1.5 font-medium text-right">HPs</th><th className="px-2 py-1.5 font-medium text-right">Com produto</th><th className="px-2 py-1.5 font-medium text-right">Créditos</th><th className="px-2 py-1.5 font-medium text-right">Aprovados</th><th className="px-2 py-1.5 font-medium text-right">Reprovados N2</th><th className="px-2 py-1.5 font-medium text-right">Reprovados</th></tr></thead>
                <tbody>
                  {r.por_usuario.map((u) => (
                    <tr key={u.nome} className="border-b last:border-0"><td className="px-2 py-1.5 font-medium">{u.nome}</td><td className="px-2 py-1.5 text-right">{u.enderecos}</td><td className="px-2 py-1.5 text-right">{u.hps}</td><td className="px-2 py-1.5 text-right text-emerald-600">{u.com_produto}</td><td className="px-2 py-1.5 text-right">{u.creditos}</td><td className="px-2 py-1.5 text-right text-emerald-600">{u.aprovados}</td><td className="px-2 py-1.5 text-right text-red-700">{u.analise_n2 || 0}</td><td className="px-2 py-1.5 text-right text-red-600">{u.reprovados}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Endereços consultados (viabilidade) <Badge variant="outline">{dados?.viabilidade?.length || 0}</Badge></CardTitle></CardHeader>
        <CardContent><TabelaViabilidade itens={dados?.viabilidade || []} /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4" /> Checagens de crédito <Badge variant="outline">{dados?.credito?.length || 0}</Badge></CardTitle></CardHeader>
        <CardContent><TabelaCredito itens={dados?.credito || []} navigate={navigate} /></CardContent>
      </Card>
    </div>
  );
};
